// "Who's responsible" display helpers shared by the plan editor
// (DefensePlanPanel.web.tsx), the viewer (DefensePlanViewer.web.tsx /
// DefensePlanViewerCard.web.tsx), and the shared row
// (DefensePlanFeatureRow.web.tsx), so the three can't drift on what
// "4 of 7 assigned" or "CAL FIRE, Jane Doe +1" means. A defense can have
// several parties linked to it (many-to-many); these helpers turn that set
// into the compact strings the UI shows.
import { partyShortName } from '../components/map/planParties';
import { effectiveBundleTeam, effectiveBundleLeader } from './planBundles';
import type {
  DefensePlanAssignment,
  PlanBundleTeamMember,
  PlanParty,
  PlanAssignmentStatus,
} from '../types';

export interface AssignmentSummary {
  total: number;
  haveSomeone: number;
  done: number;
}

export function linksForFeature(
  featureId: string,
  assignments: Record<string, DefensePlanAssignment>
): DefensePlanAssignment[] {
  return Object.values(assignments).filter((a) => a.featureId === featureId);
}

// Header summary counts DEFENSES, not parties: a defense "has someone" once
// it has at least one linked party, and counts as "done" only once every
// party linked to it is done.
export function summarizeAssignments(
  features: { id: string }[],
  assignments: Record<string, DefensePlanAssignment>
): AssignmentSummary {
  let haveSomeone = 0;
  let done = 0;
  for (const feature of features) {
    const links = linksForFeature(feature.id, assignments);
    if (links.length === 0) continue;
    haveSomeone++;
    if (links.every((l) => l.status === 'done')) done++;
  }
  return { total: features.length, haveSomeone, done };
}

// Three shapes, picked in this order:
// - every defense done: "All 7 done";
// - nothing assigned yet: "None assigned yet" ("Not assigned yet" for one);
// - otherwise: "4 of 7 assigned", plus " · 1 done" once something is.
// "0 assigned" read as awkward with nothing to compare it to — these three
// all read as plain sentences instead.
//
// `long` spells out the "need someone" shape for the panel header, which
// Glenn heard on a call as "two need someone saved", so the header keeps the
// full sentence. The short row/bundle form was "2 need someone"; on Oct 1
// 2026 Glenn asked what it meant, so it now says "None assigned yet", and a
// single feature row says "Not assigned yet" to match. The other two shapes
// are unchanged either way.
export function assignmentSummaryText(
  summary: AssignmentSummary,
  options: { long?: boolean } = {}
): string {
  const { total, haveSomeone, done } = summary;
  if (total > 0 && done === total) return `All ${total} done`;
  if (haveSomeone === 0) {
    if (options.long) {
      const noun = total === 1 ? 'defense' : 'defenses';
      const verb = total === 1 ? 'needs' : 'need';
      return `${total} ${noun} ${verb} someone assigned`;
    }
    return total > 1 ? 'None assigned yet' : 'Not assigned yet';
  }
  const parts = [`${haveSomeone} of ${total} assigned`];
  if (done > 0) parts.push(`${done} done`);
  return parts.join(' · ');
}

export const ASSIGNMENT_STATUS_LABEL: Record<PlanAssignmentStatus, string> = {
  asked: 'Asked',
  accepted: 'Accepted',
  done: 'Done',
  declined: 'Declined',
};

// "CAL FIRE, Jane Doe +1" — up to the first two linked parties' names,
// comma-joined, plus a count of any further ones. null when nothing is
// linked ("Not assigned yet").
export function featureWhoText(
  links: DefensePlanAssignment[],
  parties: Record<string, PlanParty>
): string | null {
  if (links.length === 0) return null;
  const names = links.map((l) => {
    const party = parties[l.partyId];
    return party ? partyShortName(party) : 'Unknown party';
  });
  const shown = names.slice(0, 2);
  const extra = names.length - shown.length;
  return extra > 0 ? `${shown.join(', ')} +${extra}` : shown.join(', ');
}

export interface FeatureStatusDisplay {
  text: string;
  tone: 'success' | 'danger' | 'default';
}

// Design B: color carries meaning, not surface. All done reads success, all
// declined reads danger; anything mixed is a compact status breakdown
// ("2 accepted, 1 asked") in ink. A single party just gets its plain status
// word ("Asked"), never "1 asked".
const STATUS_ORDER: PlanAssignmentStatus[] = ['done', 'accepted', 'asked', 'declined'];

export function featureStatusDisplay(links: DefensePlanAssignment[]): FeatureStatusDisplay | null {
  if (links.length === 0) return null;
  if (links.length === 1) {
    const status = links[0].status;
    const tone = status === 'done' ? 'success' : status === 'declined' ? 'danger' : 'default';
    return { text: ASSIGNMENT_STATUS_LABEL[status], tone };
  }
  if (links.every((l) => l.status === 'done')) return { text: 'Done', tone: 'success' };
  if (links.every((l) => l.status === 'declined')) return { text: 'Declined', tone: 'danger' };
  const counts: Record<PlanAssignmentStatus, number> = {
    asked: 0,
    accepted: 0,
    done: 0,
    declined: 0,
  };
  for (const link of links) counts[link.status]++;
  const text = STATUS_ORDER.filter((status) => counts[status] > 0)
    .map((status) => `${counts[status]} ${ASSIGNMENT_STATUS_LABEL[status].toLowerCase()}`)
    .join(', ');
  return { text, tone: 'default' };
}

// "Laura Dyberg (leader), Jane Doe +1": a bundle's team for the bundle row.
// The leader comes first, tagged; then the rest in team order. Like
// featureWhoText it names at most two people and counts the rest. null for an
// empty team, so the row falls back to the "2 of 5 assigned" summary. A
// leaderPartyId that is not on the team is ignored.
export function bundleWhoText(
  team: { partyId: string }[],
  leaderPartyId: string | undefined,
  parties: Record<string, PlanParty>
): string | null {
  if (team.length === 0) return null;
  const leader = leaderPartyId ? team.find((t) => t.partyId === leaderPartyId) : undefined;
  const ordered = leader ? [leader, ...team.filter((t) => t !== leader)] : team;
  const names = ordered.map((t) => {
    const party = parties[t.partyId];
    const name = party ? partyShortName(party) : 'Unknown party';
    return t === leader ? `${name} (leader)` : name;
  });
  const shown = names.slice(0, 2);
  const extra = names.length - shown.length;
  return extra > 0 ? `${shown.join(', ')} +${extra}` : shown.join(', ');
}

// The text a bundle row shows on its right: the team ("Laura (leader), Jane")
// plus " · N done" once any member defense is done, or the plain assignment
// summary for a bundle with no team. Takes the bundle and its members and
// resolves the team itself (stored, else derived), so the editor panel, the
// desktop viewer and the phone card can't drift.
export function bundleRowWhoText(
  bundle: { team?: PlanBundleTeamMember[]; leaderPartyId?: string },
  members: { id: string }[],
  assignments: Record<string, DefensePlanAssignment>,
  parties: Record<string, PlanParty>
): string {
  const summary = summarizeAssignments(members, assignments);
  const team = effectiveBundleTeam(bundle, members, assignments);
  const who = bundleWhoText(team, effectiveBundleLeader(bundle, team), parties);
  if (!who) return assignmentSummaryText(summary);
  return summary.done > 0 ? `${who} · ${summary.done} done` : who;
}
